import { FilePlus2 } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'

import { Empty } from '@/components/feedback/Empty'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatDateTime } from '@/lib/format'
import { messages } from '@/lib/messages'
import { monthlyBankReport } from '@/lib/report-document.fixtures'
import { useAuthStore } from '@/stores/auth-store'
import { useCreateReport, useDocumentTemplates } from '@/services/bi/reports'

const t = messages.documents.list

/** Boş şablon: tek paragraflı, filtresiz, sorgusuz (şemadan geçer). */
const blankDocument = () => ({
  kind: 'document' as const,
  title: t.blankTitle,
  orientation: 'portrait' as const,
  filters: [],
  queries: {},
  content: {
    type: 'doc',
    content: [{ type: 'paragraph', content: [{ type: 'text', text: t.blankParagraph }] }],
  },
})

/**
 * Belge şablonları (`/belgeler`): kullanıcının görebildiği `kind: "document"` raporlar.
 * Okuyucu listeyi görür; düzenleme ve "Yeni belge" `can_edit` / `report.publish` ile açılır.
 */
export function DocumentListPage() {
  const navigate = useNavigate()
  const templates = useDocumentTemplates()
  // Yalnız görsel kapı: karar backend'de (AGENTS.md §4).
  const canCreate = useAuthStore((state) => state.hasPermission('report.publish'))
  const create = useCreateReport()

  const start = (definition: object) =>
    create.mutate(
      { definition },
      { onSuccess: (report) => void navigate(`/belgeler/${report.id}/duzenle`) },
    )

  const items = [...(templates.data ?? [])].sort((a, b) => b.updated_at.localeCompare(a.updated_at))

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">{t.title}</h1>
          <p className="text-muted-foreground text-sm">{t.description}</p>
        </div>
        {canCreate && (
          <DropdownMenu>
            <DropdownMenuTrigger render={<Button disabled={create.isPending} />}>
              <FilePlus2 />
              {t.newDocument}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuItem onClick={() => start(blankDocument())}>
                {t.startBlank}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => start(monthlyBankReport)}>
                {t.startPilot}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {create.isError && (
        <p role="alert" className="text-destructive text-sm">
          {t.createFailed}
        </p>
      )}

      {templates.isLoading ? (
        <Loading rows={4} />
      ) : templates.isError ? (
        <ErrorState error={templates.error} onRetry={() => void templates.refetch()} />
      ) : items.length === 0 ? (
        <Empty title={t.empty} hint={canCreate ? t.emptyHint : t.emptyReadOnlyHint} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t.columns.title}</TableHead>
              <TableHead>{t.columns.visibility}</TableHead>
              <TableHead>{t.columns.updated}</TableHead>
              <TableHead className="text-right">{t.columns.version}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((report) => (
              <TableRow key={report.id}>
                <TableCell className="font-medium">
                  {String(report.definition.title ?? '')}
                </TableCell>
                <TableCell>
                  <Badge variant="secondary">{t.visibility[report.visibility]}</Badge>
                </TableCell>
                <TableCell>{formatDateTime(report.updated_at)}</TableCell>
                <TableCell className="tabular text-right">{report.version}</TableCell>
                <TableCell className="space-x-2 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    render={<Link to={`/belgeler/${report.id}`} />}
                    nativeButton={false}
                  >
                    {messages.documents.runs.generate.copies}
                  </Button>
                  {report.can_edit ? (
                    <Button
                      size="sm"
                      variant="outline"
                      render={<Link to={`/belgeler/${report.id}/duzenle`} />}
                      nativeButton={false}
                    >
                      {t.edit}
                    </Button>
                  ) : (
                    <span className="text-muted-foreground text-xs">{t.readOnly}</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
